import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { TeamsAvailability, TeamsChatSummary, TeamsMessage } from '@atlas/shared';
import { TeamsBackIcon } from '@atlas/ui';
import { formatRelativeTime } from '@/utils/relativeTime';
import { TeamsAvatar } from './TeamsAvatar';
import { TeamsCallButtons } from './TeamsCallButtons';
import { TeamsMessageContent } from './TeamsMessageContent';
import { TeamsPresenceDot } from './TeamsPresenceDot';

/**
 * Prefix for the id of a locally-echoed message that Graph has not confirmed.
 * Graph message ids are numeric tick strings, so this cannot collide with a
 * real one — which matters because the id is also the React key and the marker
 * the thread uses to render the bubble as still in flight.
 */
export const OPTIMISTIC_MESSAGE_ID_PREFIX = 'atlas-pending:';

/**
 * How close to the bottom still counts as "reading the latest".
 *
 * Not zero: a couple of pixels of sub-pixel rounding, or a partially visible
 * last bubble, should not be read as "the user has scrolled away" and strand
 * them mid-thread when the next message lands.
 */
const PINNED_TO_BOTTOM_SLACK_PX = 80;

export function isOptimisticMessage(message: TeamsMessage): boolean {
  return message.id.startsWith(OPTIMISTIC_MESSAGE_ID_PREFIX);
}

interface TeamsChatThreadProps {
  chat: TeamsChatSummary;
  /** Newest-first, exactly as the API returns it. Reversed here for display. */
  messages: TeamsMessage[];
  isLoading: boolean;
  errorMessage?: string;
  /**
   * The other person's availability, for a 1:1 only. Absent when the token
   * lacks `Presence.Read.All`, when the lookup failed, or for a group chat.
   */
  presence?: TeamsAvailability;
  /** False unless the token carries `User.ReadBasic.All` — see `TeamsAvatar`. */
  avatarsAvailable?: boolean;
  /** False on tokens predating the 2026-07-22 `Files.*` grant — no downloads. */
  filesAvailable?: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onBack: () => void;
  onRetry: () => void;
}

interface MessageRowProps {
  message: TeamsMessage;
  avatarsAvailable?: boolean;
  chatId: string;
  filesAvailable?: boolean;
}

/**
 * A single message.
 *
 * Two rendering paths, and both are DATA — never markup. `message.nodes` is a
 * typed node tree rendered one component per node type by `TeamsMessageContent`;
 * `message.text` is PLAIN TEXT, the fallback for older cached payloads and for
 * system/deleted messages, rendered as text with newlines preserved in CSS.
 * Never introduce `dangerouslySetInnerHTML` on either path.
 */
function MessageRow({ message, chatId, avatarsAvailable, filesAvailable }: MessageRowProps) {
  const { t } = useTranslation();

  if (message.isSystemMessage) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          textAlign: 'center',
          px: 2,
          py: 0.5,
          fontStyle: 'italic',
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
        }}
      >
        {message.deleted
          ? t('teams.panel.deletedMessage')
          : message.text || t('teams.panel.emptyMessage')}
      </Typography>
    );
  }

  const isSelf = message.isSelf;
  const isPending = isOptimisticMessage(message);
  /**
   * Rich nodes win when the server sent them — but never for a deleted message,
   * whose copy is the deletion notice regardless of what any cached body held.
   *
   * This also fixes the card-only case: such a message has an EMPTY `text`, so
   * the plain-text path below would call it a placeholder and show "(no text)".
   */
  const richNodes =
    !message.deleted && message.nodes && message.nodes.length > 0 ? message.nodes : undefined;
  const isPlaceholder = message.deleted || message.text.trim().length === 0;
  const bodyText = message.deleted
    ? t('teams.panel.deletedMessage')
    : message.text.trim().length === 0
      ? t('teams.panel.emptyMessage')
      : message.text;

  return (
    <Stack
      spacing={0.25}
      sx={{
        alignItems: isSelf ? 'flex-end' : 'flex-start',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ maxWidth: '100%', minWidth: 0, alignItems: 'center', px: 0.5 }}
      >
        {!isSelf ? (
          <>
            {/* #343. Only on other people's messages: your own side of the
                thread is already unambiguous, and a photo of yourself on every
                bubble is noise. */}
            <TeamsAvatar
              name={message.authorName}
              entraOid={message.authorId}
              avatarsAvailable={avatarsAvailable}
              size={20}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              title={message.authorName}
            >
              {message.authorName}
            </Typography>
          </>
        ) : null}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {isPending ? t('teams.panel.sending') : formatRelativeTime(message.createdAt)}
        </Typography>
      </Stack>

      <Box
        // A pending echo is deliberately not identical to a delivered message:
        // it is dimmed and announced, so nobody mistakes "in flight" for "sent".
        aria-busy={isPending || undefined}
        sx={{
          maxWidth: '92%',
          minWidth: 0,
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          opacity: isPending ? 0.6 : 1,
          // The app theme inverts the MUI grey scale in dark mode, so a low
          // index is the neutral surface in both modes.
          bgcolor: isSelf ? 'primary.main' : 'grey.200',
          color: isSelf ? 'primary.contrastText' : 'text.primary',
        }}
      >
        {richNodes ? (
          <TeamsMessageContent
            nodes={richNodes}
            chatId={chatId}
            messageId={message.id}
            filesAvailable={filesAvailable}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              fontStyle: isPlaceholder ? 'italic' : 'normal',
              opacity: isPlaceholder ? 0.7 : 1,
            }}
          >
            {bodyText}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * Purely presentational message thread for the Teams sidebar. Fetches nothing —
 * paging, loading and errors are all driven by props.
 */
export function TeamsChatThread({
  chat,
  messages,
  isLoading,
  errorMessage,
  presence,
  avatarsAvailable,
  filesAvailable,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onBack,
  onRetry,
}: TeamsChatThreadProps) {
  const { t } = useTranslation();

  // The API returns newest-first; the thread reads oldest-at-top.
  const ordered = useMemo(() => [...messages].reverse(), [messages]);

  const scrollRef = useRef<HTMLDivElement>(null);
  /**
   * Whether the user is reading the live end of the conversation. Starts true —
   * opening a chat means "show me where we are", never "show me how it began".
   */
  const isPinnedToBottom = useRef(true);
  const previousChatId = useRef(chat.id);
  const previousNewestId = useRef<string | undefined>(undefined);
  const previousCount = useRef(0);
  const previousScrollHeight = useRef(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isPinnedToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= PINNED_TO_BOTTOM_SLACK_PX;
  }, []);

  /**
   * Keep the viewport where the reader expects it. Three distinct cases, and
   * conflating them is what makes chat threads feel broken:
   *
   * - **A different chat, or the first page arriving.** Jump to the bottom. The
   *   newest message is the whole reason the conversation was opened.
   * - **"Load older" prepended a page.** Hold the message being read still, by
   *   absorbing the height that appeared above it. Scrolling anywhere here
   *   throws away the reader's place — the thing they just asked to keep.
   * - **A new message arrived.** Follow it only if they were already at the
   *   bottom; yanking someone out of scrollback to show an incoming line is
   *   worse than letting them miss it for a moment.
   *
   * Layout effect, not effect: this runs before paint, so the correction is
   * never visible as a jump.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    // Absent while the loading, error and empty states are showing — those
    // render in place of the message list, so there is nothing to position.
    if (!el) return;

    const newestId = ordered[ordered.length - 1]?.id;
    const isDifferentChat = previousChatId.current !== chat.id;
    const isOlderPagePrepended =
      !isDifferentChat &&
      newestId !== undefined &&
      newestId === previousNewestId.current &&
      ordered.length > previousCount.current;

    if (isDifferentChat) isPinnedToBottom.current = true;

    if (isOlderPagePrepended) {
      el.scrollTop += el.scrollHeight - previousScrollHeight.current;
    } else if (isPinnedToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }

    previousChatId.current = chat.id;
    previousNewestId.current = newestId;
    previousCount.current = ordered.length;
    previousScrollHeight.current = el.scrollHeight;
  }, [chat.id, ordered]);

  return (
    <Stack sx={{ height: '100%', minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
          px: 1,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Tooltip title={t('teams.panel.backToList')}>
          <IconButton size="small" onClick={onBack} aria-label={t('teams.panel.backToList')}>
            <TeamsBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {presence ? <TeamsPresenceDot availability={presence} /> : null}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
          }}
          title={chat.title}
        >
          {chat.title}
        </Typography>
        {/* In the header, not on list rows: a call is a deliberate act on the
            conversation you are already in, and a phone icon on twenty rows is
            the clutter #343 just finished removing from this panel. */}
        <TeamsCallButtons chat={chat} />
      </Stack>

      <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {isLoading ? (
          <Stack spacing={1.5} sx={{ p: 2 }} aria-busy aria-label={t('teams.panel.loading')}>
            {[0, 1, 2, 3].map((row) => (
              <Stack key={row} spacing={0.5} sx={{ alignItems: row % 2 ? 'flex-end' : 'flex-start' }}>
                <Skeleton variant="text" width="40%" height={14} />
                <Skeleton variant="rounded" width="75%" height={44} />
              </Stack>
            ))}
          </Stack>
        ) : errorMessage ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('teams.panel.errorTitle')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                {errorMessage}
              </Typography>
            </Alert>
            <Button size="small" variant="outlined" onClick={onRetry} fullWidth>
              {t('teams.panel.retry')}
            </Button>
          </Box>
        ) : ordered.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('teams.panel.noMessages')}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.25} sx={{ p: 1.5, minWidth: 0 }}>
            {hasMore ? (
              <Button
                size="small"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                startIcon={isLoadingMore ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {t('teams.panel.loadOlder')}
              </Button>
            ) : null}
            {ordered.map((message) => (
              <MessageRow
                key={message.id}
                message={message}
                avatarsAvailable={avatarsAvailable}
                chatId={chat.id}
                filesAvailable={filesAvailable}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
